import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Home, ArrowRight, Layers, BookOpen, HelpCircle, Newspaper, Users, Building2, ArrowLeft, Compass, MapPin } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';



const quickLinks = [
  { name: 'Home', href: '/', icon: Home, description: 'Back to the homepage' },
  { name: 'Platform', href: '/platform', icon: Layers, description: 'Explore our platform' },
  { name: 'Publishers', href: '/solutions/publishers', icon: Newspaper, description: 'Solutions for publishers' },
  { name: 'Agencies', href: '/solutions/agencies', icon: Users, description: 'Solutions for agencies' },
  { name: 'Brands', href: '/solutions/brands', icon: Building2, description: 'Solutions for brands' },
  { name: 'Resources', href: '/resources', icon: BookOpen, description: 'Guides & resources' },
  { name: 'Help Center', href: '/resources/help', icon: HelpCircle, description: 'Get support' },
  { name: 'Contact', href: '/contact', icon: MapPin, description: 'Get in touch' },
];

const NotFound: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredLinks, setFilteredLinks] = useState(quickLinks);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    console.error('404 Error: User attempted to access non-existent route:', location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = quickLinks.filter(
        (link) =>
          link.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          link.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredLinks(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredLinks(quickLinks);
      setShowSuggestions(false);
    }
  }, [searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (filteredLinks.length > 0) {
      navigate(filteredLinks[0].href);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header />

      {/* Main Content */}
      <main className="relative pt-20 lg:pt-24">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 stripe-gradient-bg opacity-40" />
          <div className="absolute inset-0 grid-pattern opacity-40" />
          {/* Floating gradient orbs */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#635bff]/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#00d4ff]/10 rounded-full blur-3xl animate-float-delayed" />
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-[#ff80b5]/8 rounded-full blur-3xl animate-float-slow" />
        </div>

        {/* Hero Section */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Content */}
            <div className="order-2 lg:order-1">
              {/* Error Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#635bff]/10 dark:bg-[#635bff]/20 border border-[#635bff]/20 dark:border-[#635bff]/30 mb-6">
                <Compass className="w-4 h-4 text-[#635bff]" />
                <span className="text-sm font-medium text-[#635bff]">Error 404</span>
              </div>

              {/* Heading */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 dark:text-white leading-tight mb-6">
                Page not{' '}
                <span className="bg-gradient-to-r from-[#635bff] via-[#00d4ff] to-[#ff80b5] bg-clip-text text-transparent">
                  found
                </span>
              </h1>

              {/* Description */}
              <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed mb-4 max-w-lg">
                The page you're looking for doesn't exist or has been moved. Let's get you back on track.
              </p>

              {/* Attempted Path */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg mb-8">
                <span className="text-xs text-slate-500 dark:text-slate-400">Attempted:</span>
                <code className="text-xs font-mono text-[#635bff] dark:text-[#00d4ff]">
                  {location.pathname}
                </code>
              </div>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="relative mb-8 max-w-md">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search for a page..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#635bff] focus:border-transparent transition-all shadow-sm"
                    aria-label="Search for a page"
                  />
                </div>

                {/* Search Suggestions Dropdown */}
                {showSuggestions && filteredLinks.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl shadow-slate-200/50 dark:shadow-black/30 overflow-hidden z-10">
                    {filteredLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-[#635bff] transition-colors"
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#635bff]/10 dark:bg-[#635bff]/20 flex items-center justify-center flex-shrink-0">
                          <link.icon className="w-4 h-4 text-[#635bff]" />
                        </div>
                        <div>
                          <p className="font-medium">{link.name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{link.description}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 ml-auto text-slate-400" />
                      </Link>
                    ))}
                  </div>
                )}

                {showSuggestions && filteredLinks.length === 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl p-4 text-center z-10">
                    <p className="text-sm text-slate-500 dark:text-slate-400">No pages found matching your search.</p>
                  </div>
                )}
              </form>

              {/* Primary CTAs */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full hover:border-[#635bff] hover:text-[#635bff] transition-all shadow-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Go Back
                </button>
                <Link
                  to="/"
                  className="stripe-button inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white rounded-full"
                >
                  <Home className="w-4 h-4" />
                  Return Home
                </Link>
                <Link
                  to="/demo"
                  className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium text-[#635bff] bg-[#635bff]/10 dark:bg-[#635bff]/20 rounded-full hover:bg-[#635bff]/20 dark:hover:bg-[#635bff]/30 transition-all"
                >
                  Request Demo
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>


            {/* Right: White space */}
            <div className="order-1 lg:order-2 hidden lg:block" />

          </div>
        </section>

        {/* Quick Links Grid */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24">
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
              Popular destinations
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Here are some pages that might help you find what you're looking for.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="group p-5 bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700/80 hover:border-[#635bff]/50 dark:hover:border-[#635bff]/50 hover:shadow-lg hover:shadow-[#635bff]/5 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#635bff]/10 to-[#7c3aed]/10 dark:from-[#635bff]/20 dark:to-[#7c3aed]/20 flex items-center justify-center flex-shrink-0 group-hover:from-[#635bff]/20 group-hover:to-[#7c3aed]/20 transition-all">
                    <link.icon className="w-5 h-5 text-[#635bff]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 dark:text-white group-hover:text-[#635bff] transition-colors">
                      {link.name}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                      {link.description}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-[#635bff] group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Help CTA Banner */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-800 dark:to-slate-900 rounded-3xl p-8 md:p-12 overflow-hidden relative">
            {/* Background pattern */}
            <div className="absolute inset-0 grid-pattern opacity-20" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#635bff]/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#00d4ff]/15 rounded-full blur-3xl" />
            
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                  Still can't find what you need?
                </h3>
                <p className="text-slate-400 max-w-lg">
                  Our support team is here to help. Reach out and we'll point you in the right direction.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 flex-shrink-0">
                <Link
                  to="/resources/help"
                  className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium text-white bg-white/10 border border-white/20 rounded-full hover:bg-white/20 transition-all"
                >
                  <HelpCircle className="w-4 h-4" />
                  Help Center
                </Link>
                <Link
                  to="/contact"
                  className="stripe-button inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white rounded-full"
                >
                  Contact Support
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NotFound;
